import React, { useState, useEffect } from "react";
import { firmApi } from "../lib/api";
import { Tile, Card, KV, MoneyInput, PctInput } from "../components/shared";
import { PlatformsCard } from "../components/PlatformsCard";
import { fmt$ } from "../lib/model";
import { Trash2, Plus, Save, Mail, Phone, MapPin, Camera } from "lucide-react";
import { toast } from "sonner";

const emptyHolding = { name: "", sector: "", acquired: "", revenue: "", sde: "", ownership: 100, status: "Active" };

export default function Firm() {
  const [firm, setFirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nh, setNh] = useState(emptyHolding);

  useEffect(() => { firmApi.get().then(setFirm); }, []);

  if (!firm) return <div className="page"><div className="page-title">Firm Profile</div><div className="page-sub">Loading…</div></div>;

  const set = (k, v) => setFirm((f) => ({ ...f, [k]: v }));
  const setCrit = (k, v) => setFirm((f) => ({ ...f, criteria: { ...f.criteria, [k]: v } }));
  const setTeam = (i, k, v) => setFirm((f) => ({ ...f, team: (f.team || []).map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const holdings = firm.holdings || [];
  const uploadPhoto = (i, file) => {
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const S = 320, c = document.createElement("canvas"); c.width = S; c.height = S;
      const s = Math.min(img.width, img.height), sx = (img.width - s) / 2, sy = Math.max(0, (img.height - s) / 4);
      c.getContext("2d").drawImage(img, sx, sy, s, s, 0, 0, S, S);
      setTeam(i, "photo", c.toDataURL("image/jpeg", 0.85));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  };
  const totalRev = holdings.reduce((a, h) => a + (Number(h.revenue) || 0), 0);
  const totalSde = holdings.reduce((a, h) => a + (Number(h.sde) || 0), 0);

  const save = async () => {
    setSaving(true);
    try { const updated = await firmApi.update(firm); setFirm(updated); toast.success("Firm profile saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addHolding = () => {
    if (!nh.name) return toast.error("Holding name required");
    setFirm((f) => ({ ...f, holdings: [...(f.holdings || []), { ...nh, revenue: Number(nh.revenue) || 0, sde: Number(nh.sde) || 0, ownership: Number(nh.ownership) || 0 }] }));
    setNh(emptyHolding);
  };
  const removeHolding = (i) => setFirm((f) => ({ ...f, holdings: f.holdings.filter((_, idx) => idx !== i) }));

  return (
    <div className="page" data-testid="page-firm">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="page-title">{firm.name}</div>
          <div className="page-sub">{firm.tagline}</div>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="firm-save"><Save size={13} style={{ marginRight: 4 }} />{saving ? "Saving…" : "Save Profile"}</button>
      </div>

      <div className="grid g4" style={{ marginBottom: 16 }}>
        <Tile label="Portfolio Companies" value={holdings.length} testid="firm-count" />
        <Tile label="Total Platform Revenue" value={fmt$(totalRev)} tone="go" testid="firm-total-rev" />
        <Tile label="Total Platform SDE" value={fmt$(totalSde)} tone="go" />
        <Tile label="AUM · Dry Powder" value={fmt$(firm.aum)} sub={`${fmt$(firm.dryPowder)} dry powder`} />
      </div>

      <div className="grid g2" style={{ alignItems: "start" }}>
        <Card title="Firm Bio">
          <textarea className="inp" rows={4} value={firm.bio} onChange={(e) => set("bio", e.target.value)} data-testid="firm-bio" />
          <div className="field" style={{ marginTop: 8 }}><label>About the firm — deal-room Team page (paragraphs)</label>
            <textarea className="inp" rows={6} value={firm.mission || ""} onChange={(e) => set("mission", e.target.value)} placeholder="Founded in 2024, XYZ Ventures is an impact-driven independent sponsor…" data-testid="firm-mission" /></div>
        </Card>

        <Card title="Analyst / Contact">
          <div className="field"><label>Analyst name</label><input className="inp" value={firm.analystName} onChange={(e) => set("analystName", e.target.value)} data-testid="firm-analyst" /></div>
          <div className="grid g2">
            <div className="field"><label>Title</label><input className="inp" value={firm.analystTitle} onChange={(e) => set("analystTitle", e.target.value)} /></div>
            <div className="field"><label>Phone</label><input className="inp" value={firm.phone} onChange={(e) => set("phone", e.target.value)} data-testid="firm-phone" /></div>
          </div>
          <div className="field"><label>Email</label><input className="inp" value={firm.email} onChange={(e) => set("email", e.target.value)} data-testid="firm-email" /></div>
          <div className="field"><label>Address</label><input className="inp" value={firm.address} onChange={(e) => set("address", e.target.value)} /></div>
          <div className="field"><label>Deal room link domain</label>
            <input className="inp mono" value={firm.roomBaseUrl ?? ""} onChange={(e) => set("roomBaseUrl", e.target.value)} placeholder="https://deals.xyzvtr.com" data-testid="firm-room-base" />
            <span className="ra-sub">Shared room links become <b>{(firm.roomBaseUrl || "https://deals.xyzvtr.com").replace(/\/+$/, "")}/room/&lt;id&gt;/&lt;deal-name&gt;</b>. Leave blank to use this app's own URL. Rooms you publish while testing in the preview environment keep preview links, because preview and the live site have separate databases — publish from the live site to hand out branded links.</span>
          </div>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, color: "var(--text2)", display: "flex", gap: 8, alignItems: "center" }}><Mail size={13} /> {firm.email}</span>
            <span style={{ fontSize: 12, color: "var(--text2)", display: "flex", gap: 8, alignItems: "center" }}><Phone size={13} /> {firm.phone}</span>
            <span style={{ fontSize: 12, color: "var(--text2)", display: "flex", gap: 8, alignItems: "center" }}><MapPin size={13} /> {firm.address}</span>
          </div>
        </Card>
      </div>

      <div className="grid g2" style={{ marginTop: 12, alignItems: "start" }}>
        <Card title="Investment Thesis">
          <textarea className="inp" rows={7} value={firm.thesis} onChange={(e) => set("thesis", e.target.value)} data-testid="firm-thesis" />
        </Card>
        <Card title="Investment Criteria">
          <div className="field"><label>Target SDE range</label><input className="inp" value={firm.criteria?.sdeRange || ""} onChange={(e) => setCrit("sdeRange", e.target.value)} /></div>
          <div className="field"><label>Sectors</label><input className="inp" value={firm.criteria?.sectors || ""} onChange={(e) => setCrit("sectors", e.target.value)} /></div>
          <div className="grid g2">
            <div className="field"><label>Geography</label><input className="inp" value={firm.criteria?.geography || ""} onChange={(e) => setCrit("geography", e.target.value)} /></div>
            <div className="field"><label>Hold period</label><input className="inp" value={firm.criteria?.hold || ""} onChange={(e) => setCrit("hold", e.target.value)} /></div>
          </div>
          <div className="field"><label>Equity check size</label><input className="inp" value={firm.criteria?.checkSize || ""} onChange={(e) => setCrit("checkSize", e.target.value)} /></div>
        </Card>
      </div>

      <Card title="Current Holdings — Platform Portfolio" style={{ marginTop: 12 }} className="table-scroll" testid="firm-holdings">
        <table className="tbl">
          <thead><tr><th>Company</th><th>Sector</th><th>Acquired</th><th className="num">Annual Revenue</th><th className="num">SDE</th><th className="num">Own %</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {holdings.map((h, i) => (
              <tr key={i} data-testid="holding-row">
                <td>{h.name}</td><td>{h.sector}</td><td>{h.acquired}</td>
                <td className="num">{fmt$(h.revenue)}</td><td className="num">{fmt$(h.sde)}</td><td className="num">{h.ownership}%</td>
                <td><span className={`pill ${["Active", "Under Operation"].includes(h.status) ? "go" : h.status === "Exited" ? "" : "amber"}`}>{h.status}</span></td>
                <td><button className="btn" style={{ padding: "3px 6px" }} onClick={() => removeHolding(i)} data-testid={`holding-del-${i}`}><Trash2 size={13} /></button></td>
              </tr>
            ))}
            <tr className="total"><td>Platform Total</td><td /><td /><td className="num">{fmt$(totalRev)}</td><td className="num">{fmt$(totalSde)}</td><td /><td /><td /></tr>
          </tbody>
        </table>

        <div className="section-label">Add holding</div>
        <div className="grid g4" style={{ gap: 8 }}>
          <input className="inp" placeholder="Company / project name" value={nh.name} onChange={(e) => setNh({ ...nh, name: e.target.value })} data-testid="nh-name" />
          <input className="inp" placeholder="Sector" value={nh.sector} onChange={(e) => setNh({ ...nh, sector: e.target.value })} data-testid="nh-sector" />
          <input className="inp" placeholder="Acquired (year)" value={nh.acquired} onChange={(e) => setNh({ ...nh, acquired: e.target.value })} data-testid="nh-acquired" />
          <select className="inp" value={nh.status} onChange={(e) => setNh({ ...nh, status: e.target.value })}>{["Active", "Under LOI", "Closed", "Under Operation", "Integrating", "Exited"].map((s) => <option key={s}>{s}</option>)}</select>
          <MoneyInput value={nh.revenue} onChange={(v) => setNh({ ...nh, revenue: v })} placeholder="Annual revenue" testid="nh-revenue" />
          <MoneyInput value={nh.sde} onChange={(v) => setNh({ ...nh, sde: v })} placeholder="SDE" testid="nh-sde" />
          <PctInput value={nh.ownership} onChange={(v) => setNh({ ...nh, ownership: v })} placeholder="Ownership %" testid="nh-ownership" />
          <button className="btn btn-primary" onClick={addHolding} data-testid="nh-add"><Plus size={13} style={{ marginRight: 4 }} />Add</button>
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 10 }}>Remember to click <b>Save Profile</b> to persist changes.</div>
      </Card>
      <Card title="Team — shown in investor deal rooms" testid="firm-team" className="ir-gap">
        <div className="ra-sub" style={{ marginBottom: 8 }}>This roster loads into every deal room's Team screen. Investors see the name, role, photo and bio.</div>
        {(firm.team || []).map((p, i) => (
          <div className="team-edit-row" key={i} data-testid={`firm-team-row-${i}`}>
            <input className="inp" placeholder="Name" value={p.name || ""} onChange={(e) => setTeam(i, "name", e.target.value)} data-testid={`firm-team-name-${i}`} />
            <input className="inp" placeholder="Role" value={p.role || ""} onChange={(e) => setTeam(i, "role", e.target.value)} data-testid={`firm-team-role-${i}`} />
            <label className="btn btn-sm team-photo-btn" data-testid={`firm-team-upload-${i}`}>
              {p.photo ? <img src={p.photo} alt="" /> : <Camera size={12} />}{p.photo ? "Replace photo" : "Upload photo"}
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadPhoto(i, e.target.files?.[0])} data-testid={`firm-team-photo-${i}`} />
            </label>
            <textarea className="inp" rows={4} placeholder="Background — full bio shown in the deal room" value={p.bio || ""} onChange={(e) => setTeam(i, "bio", e.target.value)} data-testid={`firm-team-bio-${i}`} />
            <button className="icon-btn" onClick={() => set("team", (firm.team || []).filter((_, j) => j !== i))} data-testid={`firm-team-del-${i}`}><Trash2 size={12} /></button>
          </div>
        ))}
        <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => set("team", [...(firm.team || []), { name: "", role: "", bio: "", photo: "" }])} data-testid="firm-team-add"><Plus size={12} style={{ marginRight: 4 }} />Add person</button>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 10 }}>Remember to click <b>Save Profile</b> to persist changes.</div>
      </Card>
      <div style={{ marginTop: 12 }}><PlatformsCard /></div>
    </div>
  );
}
